import React, { useId } from "react"
import { useTranslation } from "react-i18next"

import { type CheckState } from "../../helpers/notification-settings-matrix.ts"

interface CategoryToggleProps {
  state: CheckState
  disabled: boolean
  onChange: (next: boolean) => void
}

// A switch can only say on or off, and a category is often partly on, so this
// is a tri-state checkbox drawn as a toggle: mixed sits in the middle with a
// dash, and pressing it from mixed turns everything on.
export function CategoryToggle({
  state,
  disabled,
  onChange
}: CategoryToggleProps) {
  const { t } = useTranslation()
  const statusId = useId()
  const on = state === "checked"
  const mixed = state === "mixed"

  const status = mixed
    ? t("notifications.settings.someEnabled")
    : on
      ? t("notifications.settings.allEnabled")
      : t("notifications.settings.noneEnabled")

  return (
    <div className="flex items-center gap-3 shrink-0">
      <button
        type="button"
        role="checkbox"
        aria-checked={mixed ? "mixed" : on}
        aria-label={t("notifications.settings.allNotifications")}
        aria-describedby={statusId}
        disabled={disabled}
        onClick={() => onChange(!on)}
        data-testid="category-toggle"
        className={`relative inline-flex h-7 w-[52px] shrink-0 items-center rounded-full transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 ${
          on || mixed ? "bg-primary" : "bg-gray-300"
        }`}
      >
        <span
          aria-hidden="true"
          className={`absolute top-1 size-5 rounded-full bg-white shadow transition-[left] flex items-center justify-center ${
            on ? "left-[28px]" : mixed ? "left-[16px]" : "left-1"
          }`}
        >
          {mixed && <span className="block h-0.5 w-2.5 rounded bg-primary" />}
        </span>
      </button>
      <span className="flex flex-col leading-tight">
        <span className="text-sm font-medium text-gray-800">
          {t("notifications.settings.allNotifications")}
        </span>
        <span id={statusId} className="text-xs text-gray-500">
          {status}
          <span className="sr-only">. </span>
          <span className="block">
            {t("notifications.settings.categoryOnly")}
          </span>
        </span>
      </span>
    </div>
  )
}
